/**
 * BRE-9699: the web Lambda's function URL answers a request whose headers pass
 * ~9 KB with a 413, and Next shows the user only "An unexpected response was
 * received from the server" (or nothing, for a prefetch). The headers that grow
 * are cookies, so this tells the user once per page and offers the fix: clear
 * the cookies no server code reads, or sign out when that was already tried.
 */

import { captureProductClientEvent } from "@/lib/analytics/product-client-event";
import { toast } from "sonner";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { bufferOrCapture } from "@/lib/analytics/capture-buffer";
import { expireCookie, readVisibleCookieNames, resetBrowserDataAndSignOut } from "@/lib/browser-data-reset";

/**
 * Intercom, Google Analytics/Ads and the brev.io marketing site's widget set
 * these on `.brev.io`; only their own scripts read them. Never widen this to a
 * cookie the server reads (`wos-*`, `_gcl_aw`, `brev_*`, OAuth state cookies,
 * `ph_*_posthog` which proxy.ts reads for session-loss logs): clearing one
 * breaks sign-in, signup attribution, an OAuth flow or a log join.
 */
const CLEARABLE_COOKIE = /^(intercom-|wc_|_ga$|_ga_|_gcl_au$)/;

/** When this tab last cleared; a repeat overflow soon after offers sign-out instead of a clear that did not help. */
const CLEARED_AT_KEY = "brev_header_overflow_cleared_at";
const CLEARED_WINDOW_MS = 10 * 60_000;
/** An RSC navigation's 413 makes Next reload the document, so the toast waits for the next page. */
const PENDING_KEY = "brev_header_overflow_pending";

export type HeaderOverflowRequestKind = "server-action" | "rsc-prefetch" | "rsc-navigation";

let surfaced = false;

export function clearableCookieNames(cookieHeader: string): string[] {
  return readVisibleCookieNames(cookieHeader).filter((name) => CLEARABLE_COOKIE.test(name));
}

function clearedRecently(): boolean {
  try {
    const at = Number(sessionStorage.getItem(CLEARED_AT_KEY));
    return at > 0 && Date.now() - at < CLEARED_WINDOW_MS;
  } catch {
    return false;
  }
}

function clearAndReload(names: string[]): void {
  for (const name of names) {
    try {
      expireCookie(name);
    } catch {
      // Keep going: the reload is what the user asked for.
    }
  }
  try {
    sessionStorage.setItem(CLEARED_AT_KEY, String(Date.now()));
  } catch {
    // Without the marker a repeat overflow offers the clear again; still safe.
  }
  window.location.reload();
}

/** Show the overflow toast at most once per page load. Never throws. */
export function surfaceHeaderOverflow(requestKind: HeaderOverflowRequestKind): void {
  if (surfaced || typeof window === "undefined") return;
  surfaced = true;
  try {
    const names = clearableCookieNames(document.cookie);
    const offerClear = names.length > 0 && !clearedRecently();
    bufferOrCapture(
      captureProductClientEvent,
      ANALYTICS_EVENTS.HEADER_OVERFLOW_SURFACED,
      { request_kind: requestKind, clearable_cookie_count: names.length, remedy: offerClear ? "clear" : "sign-out" },
    );
    toast.error("Brev couldn't load this.", {
      description: "Your browser has too many cookies.",
      duration: Infinity,
      action: offerClear
        ? { label: "Clear and reload", onClick: () => clearAndReload(names) }
        : { label: "Sign out", onClick: resetBrowserDataAndSignOut },
    });
  } catch {
    // A failed toast must not break the request that triggered it.
  }
}

/** Record an RSC navigation's overflow for the page Next is about to load in its place. Never throws. */
export function deferHeaderOverflowToNextPage(): void {
  try {
    sessionStorage.setItem(PENDING_KEY, "1");
  } catch {
    // Storage unavailable: the user sees what Next shows, as before.
  }
}

/** Call once after mount: shows the toast an RSC navigation's overflow deferred to this page. */
export function showPendingHeaderOverflow(): void {
  try {
    if (sessionStorage.getItem(PENDING_KEY) === null) return;
    sessionStorage.removeItem(PENDING_KEY);
  } catch {
    return;
  }
  // After the commit's other effects, so sonner's <Toaster> has subscribed.
  window.setTimeout(() => surfaceHeaderOverflow("rsc-navigation"), 0);
}

/** Test seam. */
export function resetHeaderOverflowRecovery(): void {
  surfaced = false;
}
